"use client";

import { TZDate } from "@date-fns/tz";
import {
  Calendar,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Crown,
  FileText,
  HelpCircle,
  MapPin,
  MapPinOff,
  Sparkles,
  Star,
  Users,
} from "lucide-react";
import dynamic from "next/dynamic";
import {
  type ComponentType,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { PackForSaleRow } from "@/actions/packs";
import {
  ContactDetailsDrawer,
  ContactDetailsRow,
} from "@/components/contact-details-sheet";
import { BookingWindowRow } from "@/components/events/booking-window-row";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { useIsClampedOverflow } from "@/components/ui/clamped-text.client";
import {
  DETAIL_INFO_GROUP_ROW,
  DetailInfoGroup,
  DetailInfoRow,
} from "@/components/ui/detail-info-row";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Skeleton } from "@/components/ui/skeleton";
import { EventAddonsShowcase } from "@/features/event-addons/components/event-addons-showcase.client";
import {
  CollaboratorsCard,
  HostedByCard,
} from "@/features/hiives/components/hosted-by-card";
import { PackEligibilityNote } from "@/features/packs/components/pack-eligibility";
import type { PackStripContext } from "@/features/packs/components/pack-strip";
import { PackWalletCards } from "@/features/packs/components/pack-wallet-cards";
import type { ViewerPackCoverage } from "@/features/packs/lib/eligibility";
import { perBookingMinor, savingAgainst } from "@/features/packs/lib/pricing";
import { useNow } from "@/hooks/use-now";
import { eventAvailability } from "@/lib/availability";
import type { SocialLinks } from "@/lib/contact-links";
import { APP_DEFAULT_TIME_ZONE } from "@/lib/dates/zoned-input";
import {
  type BookingWindowOccurrence,
  formatCountdown,
  formatDurationWords,
  formatRelativeSpan,
  isOccurrenceSoldOut,
} from "@/lib/events/booking-window-state";
import { formatLocationDistance } from "@/lib/locations/nearby-location";
import type { MediaAsset } from "@/lib/media/types";
import { cn } from "@/lib/utils";
import type { NormalizedOccurrence } from "./event-page.shared";

const VenueMap = dynamic(
  () => import("@/components/maps/venue-map").then((mod) => mod.VenueMap),
  {
    ssr: false,
    loading: () => <Skeleton className="h-[220px] w-full rounded-2xl" />,
  },
);

interface EventDetailsContentProps {
  event: {
    description: string;
    pattern: string;
    venueName: string;
    address: string;
    hideExactLocation?: boolean;
    latitude: number | null;
    longitude: number | null;
    discoveryScope: "venue" | "areas" | "everywhere";
    visibilityAreas: Array<{ id: number; label: string }>;
    highlightStandsOut: string | null;
    highlightExperience: string | null;
    highlightSpecialAttractions: string | null;
    highlightPerfectFor: string | null;
    allowedGenders?: Array<"male" | "female" | "other">;
    contactPhone?: string | null;
    contactEmail?: string | null;
    websiteUrl?: string | null;
    socialLinks?: SocialLinks | null;
    thingsToKnow: string[];
    amenities: string[];
    termsAndConditions: Array<{ title: string; description?: string | null }>;
    faqs?: Array<{ title: string; description?: string | null }>;
    /** Accepted partner Hiives, shown under "Hosted by". */
    collaborators?: Array<{
      id: number;
      name: string;
      slug: string;
      logoImage: unknown;
    }>;
  };
  scheduleOccurrence?: NormalizedOccurrence | null;
  scheduleOccurrences?: NormalizedOccurrence[];
  distanceKm: number | null;
  hiive?: {
    name: string;
    logoImage: MediaAsset | null;
  };
  hiiveMemberCount?: number;
  hiiveEventCount?: number;
  packs?: PackForSaleRow[];
  packStripContext?: PackStripContext | null;
  viewerPackHolding?: ViewerPackCoverage | null;
  hiiveSlug?: string;
}

const formatDistance = (km: number) => `${formatLocationDistance(km)} away`;

const GENDER_GROUP_LABELS: Record<"male" | "female" | "other", string> = {
  male: "men",
  female: "women",
  other: "non-binary people",
};

/** Human label for a gender-restricted event; null when open to everyone. */
const formatAllowedGenders = (
  genders: Array<"male" | "female" | "other"> | undefined,
) => {
  if (!genders || genders.length === 0 || genders.length >= 3) return null;
  if (genders.length === 1) {
    const gender = genders[0];
    if (gender === "female") return "Women-only event";
    if (gender === "male") return "Men-only event";
    return "Non-binary-only event";
  }
  const names = genders.map((gender) => GENDER_GROUP_LABELS[gender]);
  return `Open to ${names.join(" & ")}`;
};

const SCHEDULE_TIME_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: APP_DEFAULT_TIME_ZONE,
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

const SCHEDULE_FULL_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: APP_DEFAULT_TIME_ZONE,
  weekday: "short",
  day: "numeric",
  month: "short",
  year: "numeric",
});

const SCHEDULE_SHORT_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: APP_DEFAULT_TIME_ZONE,
  weekday: "short",
  day: "numeric",
  month: "short",
});

type ScheduleTimelineItem = {
  key: string;
  kind: string;
  label: string;
  description: string | null;
  occursAt: Date;
  sortOrder: number;
};

function formatScheduleTime(date: Date) {
  return SCHEDULE_TIME_FORMATTER.format(date);
}

function isSameCalendarDay(first: Date, second: Date) {
  const left = new TZDate(first, APP_DEFAULT_TIME_ZONE);
  const right = new TZDate(second, APP_DEFAULT_TIME_ZONE);
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

function formatScheduleRange(occurrences: NormalizedOccurrence[]) {
  if (occurrences.length === 0) return null;

  const sorted = [...occurrences].sort(
    (first, second) => first.start.getTime() - second.start.getTime(),
  );
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (!first || !last) return null;

  const firstLabel = SCHEDULE_FULL_DATE_FORMATTER.format(first.start);
  const lastLabel = SCHEDULE_FULL_DATE_FORMATTER.format(last.end);
  return isSameCalendarDay(first.start, last.end)
    ? firstLabel
    : `${firstLabel} - ${lastLabel}`;
}

function buildScheduleTimelineItems(
  occurrence: NormalizedOccurrence,
): ScheduleTimelineItem[] {
  const explicitItems = occurrence.timelineItems.map((item) => ({
    key: `timeline-${item.id}`,
    kind: item.kind,
    label: item.kind === "gates_open" ? "Gates open" : item.label,
    description: item.description,
    occursAt: item.occursAt,
    sortOrder: item.sortOrder,
  }));

  const hasKind = (kind: string) =>
    explicitItems.some((item) => item.kind === kind);

  const items: ScheduleTimelineItem[] = [...explicitItems];

  if (occurrence.bookingOpensAt) {
    items.push({
      key: `booking-opens-${occurrence.id}`,
      kind: "booking_opens",
      label: "Bookings open",
      description: null,
      occursAt: occurrence.bookingOpensAt,
      sortOrder: -20,
    });
  }

  if (!hasKind("start")) {
    items.push({
      key: `start-${occurrence.id}`,
      kind: "start",
      label: "Event starts",
      description: null,
      occursAt: occurrence.start,
      sortOrder: 0,
    });
  }

  if (!hasKind("end")) {
    items.push({
      key: `end-${occurrence.id}`,
      kind: "end",
      label: "Event ends",
      description: null,
      occursAt: occurrence.end,
      sortOrder: 1,
    });
  }

  if (occurrence.bookingClosesAt) {
    items.push({
      key: `booking-closes-${occurrence.id}`,
      kind: "booking_closes",
      label: "Bookings close",
      description: null,
      occursAt: occurrence.bookingClosesAt,
      sortOrder: 20,
    });
  }

  return items.sort((first, second) => {
    const timeDelta = first.occursAt.getTime() - second.occursAt.getTime();
    if (timeDelta !== 0) return timeDelta;
    return first.sortOrder - second.sortOrder;
  });
}

/**
 * A timeline entry that has already happened reads wrong in the present tense.
 */
const PAST_TENSE_LABELS: Record<string, string> = {
  booking_opens: "Bookings opened",
  booking_closes: "Bookings closed",
  gates_open: "Gates opened",
  start: "Event started",
  end: "Event ended",
};

/**
 * The drawer's timeline, told from where the clock actually is.
 *
 * Four rows of bare times told a reader almost nothing: which day a bare
 * "9:59 AM" belonged to, which of them had already happened, and how far off
 * the rest were, all had to be worked out. So every row now carries three
 * things — what it is, how far away it is in words, and its day as well as its
 * time — and the three states are drawn differently enough to tell apart at a
 * glance: done is a filled tick, next is a ringed target under a "Now" line,
 * still to come is a hollow ring.
 *
 * Relative labels are one coarse unit ("in 18 hours"), not a countdown: the
 * absolute time sits right beside them, and a ticking "18h 25m" next to
 * "9:59 AM" only invites the reader to check the arithmetic. The exception is
 * the next entry inside the final hour, where the minutes are the point.
 *
 * Its own ticker, so nothing here re-renders the whole details tab.
 */
function ScheduleTimeline({ items }: { items: ScheduleTimelineItem[] }) {
  const now = useNow((current) => {
    const next = items.find(
      (item) => item.occursAt.getTime() > current.getTime(),
    );
    if (!next) return null;
    return next.occursAt.getTime() - current.getTime() <= 10 * 60_000
      ? 1000
      : 60_000;
  });

  const nowMs = now?.getTime() ?? null;
  const nextIndex =
    nowMs == null
      ? -1
      : items.findIndex((item) => item.occursAt.getTime() > nowMs);

  const nowMarker =
    now == null ? null : (
      <div className="grid grid-cols-[1.5rem_minmax(0,1fr)_auto] items-center gap-3 pb-5">
        <div className="relative flex justify-center">
          <span className="absolute inset-y-0 w-px bg-border dark:bg-white/24" />
        </div>
        <div className="flex items-center gap-2">
          <span className="shrink-0 rounded-full bg-brand/12 px-2 py-0.5 text-[0.65rem] font-semibold tracking-[0.08em] text-brand uppercase dark:bg-white/12 dark:text-white">
            Now
          </span>
          <span className="h-px flex-1 bg-border dark:bg-white/16" />
        </div>
        <span className="text-xs font-medium text-text-secondary tabular-nums dark:text-white/64">
          {formatScheduleTime(now)}
        </span>
      </div>
    );

  return (
    <div className="space-y-0">
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const occursAtMs = item.occursAt.getTime();
        const isPast = nowMs != null && occursAtMs <= nowMs;
        const isNext = index === nextIndex;
        const delta = nowMs == null ? null : occursAtMs - nowMs;

        const label =
          isPast && PAST_TENSE_LABELS[item.kind]
            ? PAST_TENSE_LABELS[item.kind]
            : item.label;

        const relative =
          delta == null
            ? null
            : isNext && delta < 60 * 60_000
              ? `in ${formatCountdown(delta, delta > 10 * 60_000 ? "minutes" : "seconds")}`
              : now == null
                ? null
                : formatRelativeSpan(item.occursAt, now);

        return (
          <div key={item.key}>
            {isNext ? nowMarker : null}

            <div className="grid grid-cols-[1.5rem_minmax(0,1fr)_auto] gap-3">
              <div className="relative flex justify-center">
                {isPast ? (
                  <span className="mt-1 flex h-4 w-4 items-center justify-center rounded-full bg-text-tertiary dark:bg-white/40">
                    <Check
                      className="h-2.5 w-2.5 text-surface dark:text-[#111111]"
                      strokeWidth={4}
                    />
                  </span>
                ) : isNext ? (
                  <span className="mt-1 flex h-4 w-4 items-center justify-center rounded-full border-2 border-brand dark:border-white">
                    <span className="h-1.5 w-1.5 rounded-full bg-brand dark:bg-white" />
                  </span>
                ) : (
                  <span className="mt-1 h-4 w-4 rounded-full border-2 border-border dark:border-white/30" />
                )}
                {!isLast ? (
                  <span className="absolute top-6 bottom-0 w-px bg-border dark:bg-white/24" />
                ) : null}
              </div>

              <div className="min-w-0 pb-5">
                <p
                  className={cn(
                    "truncate text-[1rem]",
                    isPast
                      ? "font-medium text-text-tertiary dark:text-white/48"
                      : isNext
                        ? "font-semibold text-text-primary dark:text-white"
                        : "font-medium text-text-primary dark:text-white/88",
                  )}
                >
                  {label}
                </p>
                {relative ? (
                  <p
                    className={cn(
                      "mt-0.5 text-xs tabular-nums",
                      isNext
                        ? "font-medium text-brand dark:text-white/72"
                        : "text-text-tertiary dark:text-white/40",
                    )}
                  >
                    {relative}
                  </p>
                ) : null}
                {item.description ? (
                  <p className="mt-1 text-sm text-text-tertiary dark:text-white/48">
                    {item.description}
                  </p>
                ) : null}
              </div>

              <div className="pb-5 text-right">
                <p
                  className={cn(
                    "text-[1rem] font-medium tabular-nums",
                    isPast
                      ? "text-text-tertiary dark:text-white/48"
                      : "text-text-primary dark:text-white/88",
                  )}
                >
                  {formatScheduleTime(item.occursAt)}
                </p>
                <p
                  className={cn(
                    "mt-0.5 text-xs",
                    isPast
                      ? "text-text-tertiary dark:text-white/32"
                      : "text-text-secondary dark:text-white/48",
                  )}
                >
                  {SCHEDULE_SHORT_DATE_FORMATTER.format(item.occursAt)}
                </p>
              </div>
            </div>
          </div>
        );
      })}

      {/* Everything has happened: the marker belongs at the end of the list,
          not nowhere. */}
      {nextIndex === -1 ? nowMarker : null}
    </div>
  );
}

/**
 * The subset of an occurrence the booking-window rule needs, plus the two facts
 * it cannot read off the row itself: when the doors open, and whether there is
 * anything left to sell.
 */
function toBookingWindowOccurrence(
  occurrence: NormalizedOccurrence,
): BookingWindowOccurrence {
  return {
    id: occurrence.id,
    start: occurrence.start,
    end: occurrence.end,
    bookingOpensAt: occurrence.bookingOpensAt,
    bookingClosesAt: occurrence.bookingClosesAt,
    gatesOpenAt:
      occurrence.timelineItems.find((item) => item.kind === "gates_open")
        ?.occursAt ?? null,
    soldOut: isOccurrenceSoldOut(occurrence),
  };
}

function getOccurrenceScheduleAnchor(occurrence: NormalizedOccurrence) {
  const gatesOpen = occurrence.timelineItems.find(
    (item) => item.kind === "gates_open",
  );
  return gatesOpen?.occursAt ?? occurrence.start;
}

const _renderListItems = (
  items: Array<{ title: string; description?: string | null }>,
) => (
  <div className="space-y-3">
    {items.map((item) => (
      <div key={`${item.title}-${item.description ?? ""}`}>
        <p className="font-medium text-text-primary">{item.title}</p>
        {item.description ? (
          <p className="text-sm text-text-secondary">{item.description}</p>
        ) : null}
      </div>
    ))}
  </div>
);

const HIGHLIGHT_CARD_CLASS =
  "relative flex h-full w-full flex-col overflow-hidden rounded-2xl border border-warning-border bg-warning-bg p-5 text-left shadow-sm dark:border-[#F5E6B8]/15 dark:bg-[#1a1d14] dark:shadow-lg";

/**
 * Highlight card with a 3-line clamp. When the text actually overflows, it
 * becomes tappable and shows a fade + "Read more" overlay that is absolutely
 * positioned, so the affordance never adds height or breaks the shared card
 * size. Tapping opens a drawer with the full text. When the text fits, it
 * renders as a plain (non-interactive) card with no affordance.
 */
function HighlightCard({
  label,
  value,
  Icon,
  onOpen,
}: {
  label: string;
  value: string;
  Icon: ComponentType<{ className?: string }>;
  onOpen: () => void;
}) {
  const textRef = useRef<HTMLParagraphElement | null>(null);
  const isTruncated = useIsClampedOverflow(textRef, value);

  // Keep a stable outer element so flipping truncated state never remounts the
  // text node (which would invalidate the ref mid-measurement).
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: this stable wrapper only becomes a keyboard-accessible button when the measured text is truncated.
    <div
      className={cn(HIGHLIGHT_CARD_CLASS, isTruncated && "cursor-pointer")}
      role={isTruncated ? "button" : undefined}
      tabIndex={isTruncated ? 0 : undefined}
      onClick={isTruncated ? onOpen : undefined}
      onKeyDown={
        isTruncated
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
    >
      <div className="pointer-events-none absolute -top-12 -right-12 h-40 w-40 rounded-full bg-warning/10 blur-3xl dark:bg-[#F5E6B8]/10" />
      <div className="relative flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-warning-border bg-warning-bg text-warning shadow-sm dark:border-[#F5E6B8]/20 dark:bg-[#F5E6B8]/10 dark:text-[#F5E6B8] dark:shadow-[0_0_15px_rgba(245,230,184,0.1)]">
          <Icon className="h-5 w-5" />
        </div>
        <p className="text-[15px] font-semibold text-text-primary dark:text-white">
          {label}
        </p>
      </div>
      <div className="relative mt-4">
        <p
          ref={textRef}
          className="line-clamp-3 text-sm leading-relaxed text-text-secondary dark:text-white/75"
        >
          {value}
        </p>
        {isTruncated ? (
          // Liquid-glass chip on the last line only — no full-width fade that
          // washes out the whole line of body text.
          <span className="pointer-events-none absolute right-0 bottom-0 pl-6">
            <span
              className={[
                "inline-flex items-center gap-0.5 rounded-full px-2.5 py-0.5",
                "border border-white/45 bg-white/55 font-semibold text-[11px] text-text-primary",
                "shadow-[inset_0_1px_0_0_rgba(255,255,255,0.55),0_1px_2px_rgba(15,23,42,0.06)]",
                "backdrop-blur-md backdrop-saturate-150",
                "dark:border-white/14 dark:bg-white/[0.08] dark:text-white",
                "dark:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.12),0_1px_2px_rgba(0,0,0,0.35)]",
              ].join(" ")}
            >
              Read more
              <ChevronRight className="h-3 w-3 opacity-80" />
            </span>
          </span>
        ) : null}
      </div>
    </div>
  );
}

export function EventDetailsContent({
  event,
  scheduleOccurrence,
  scheduleOccurrences = [],
  distanceKm,
  hiive,
  hiiveMemberCount = 0,
  hiiveEventCount = 0,
  packs = [],
  packStripContext,
  viewerPackHolding,
  hiiveSlug,
}: EventDetailsContentProps) {
  const [locationOpen, setLocationOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [expandedScheduleId, setExpandedScheduleId] = useState<number | null>(
    scheduleOccurrence?.id ?? scheduleOccurrences[0]?.id ?? null,
  );
  const [aboutOpen, setAboutOpen] = useState(false);
  const [activeHighlight, setActiveHighlight] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [thingsToKnowOpen, setThingsToKnowOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const eventContactDetails = useMemo(
    () => ({
      contactPhone: event.contactPhone ?? null,
      contactEmail: event.contactEmail ?? null,
      websiteUrl: event.websiteUrl ?? null,
      socialLinks: event.socialLinks ?? {},
    }),
    [
      event.contactPhone,
      event.contactEmail,
      event.websiteUrl,
      event.socialLinks,
    ],
  );
  const [termsOpen, setTermsOpen] = useState(false);
  const [faqsOpen, setFaqsOpen] = useState(false);
  const hasThingsToKnow = event.thingsToKnow.length > 0;
  const hasAmenities = event.amenities.length > 0;
  const hasThingsOrAmenities = hasThingsToKnow || hasAmenities;
  const previewItems = event.thingsToKnow.slice(0, 4);
  const visibleScheduleOccurrences =
    scheduleOccurrences.length > 0
      ? scheduleOccurrences
      : scheduleOccurrence
        ? [scheduleOccurrence]
        : [];
  const bookingWindowOccurrence = scheduleOccurrence
    ? toBookingWindowOccurrence(scheduleOccurrence)
    : null;
  const bookingWindowOccurrences = visibleScheduleOccurrences.map(
    toBookingWindowOccurrence,
  );
  const visibilityAreaLabels = event.visibilityAreas
    .map((area) => area.label)
    .filter(Boolean);
  const remoteDiscoveryNote =
    event.discoveryScope === "everywhere"
      ? `This event takes place in ${event.venueName} — open to attendees from anywhere`
      : event.discoveryScope === "areas" && visibilityAreaLabels.length > 0
        ? `This event takes place in ${event.venueName} — open to bookings from ${visibilityAreaLabels.join(", ")}`
        : null;
  const hasScheduleTimeline =
    Boolean(scheduleOccurrence?.bookingOpensAt) ||
    Boolean(scheduleOccurrence?.bookingClosesAt) ||
    Boolean(scheduleOccurrence?.timelineItems.length);
  const scheduleRange = formatScheduleRange(visibleScheduleOccurrences);
  const genderRestrictionLabel = formatAllowedGenders(event.allowedGenders);

  // Expand whichever occurrence the drawer is actually about. The selected one
  // wins while it still has something ahead of it; once it is over, a finished
  // date is the least useful thing to have open. Deliberately an effect rather
  // than an initial state: it reads the clock, which server-rendered markup
  // must not.
  useEffect(() => {
    const now = Date.now();
    const selected = scheduleOccurrence ?? null;
    if (selected && selected.end.getTime() > now) {
      setExpandedScheduleId(selected.id);
      return;
    }
    const nextLive = visibleScheduleOccurrences.find(
      (occurrence) => occurrence.end.getTime() > now,
    );
    setExpandedScheduleId(nextLive?.id ?? selected?.id ?? null);
  }, [scheduleOccurrence, visibleScheduleOccurrences]);

  // The best saving any sellable pack gives on this event's cheapest pass —
  // the number that makes a stranger care. Computed here rather than on the
  // server because everything it needs is already on this page.
  /*
   * Both ends of what a pack saves on this event.
   *
   * The cheapest pass and the dearest, so this page and the booking sheet quote
   * the same range — a "save 8%" here followed by "save 8–57%" one tap later
   * reads as two different offers.
   */
  const bestPackPercent = (() => {
    if (!packStripContext) return null;
    const percents: number[] = [];
    for (const pack of packs) {
      const perBooking = perBookingMinor(pack);
      for (const price of [
        packStripContext.listPriceMinor,
        packStripContext.maxListPriceMinor,
      ]) {
        const saving = savingAgainst(perBooking, price);
        if (saving) percents.push(saving.percent);
      }
    }
    if (percents.length === 0) return null;
    return { low: Math.min(...percents), high: Math.max(...percents) };
  })();

  return (
    <div className="space-y-6 pb-4">
      {/* Above everything, because it changes what the page costs. Somebody
          holding a pack must not read a price and reach for a card; somebody
          holding none will never find the packs page on their own. */}
      {packs.length > 0 && hiiveSlug ? (
        <PackEligibilityNote
          hiiveSlug={hiiveSlug}
          holding={viewerPackHolding}
          percent={bestPackPercent}
        />
      ) : null}

      <DetailInfoRow
        icon={MapPin}
        title={event.venueName}
        subtitle={
          distanceKm != null ? formatDistance(distanceKm) : event.address
        }
        note={remoteDiscoveryNote}
        onClick={() => setLocationOpen(true)}
      />

      {genderRestrictionLabel ? (
        <DetailInfoRow
          icon={Users}
          title={genderRestrictionLabel}
          subtitle="Only matching profiles can book this event"
        />
      ) : null}

      {scheduleOccurrence?.status === "scheduled" &&
      eventAvailability(scheduleOccurrence).label ? (
        <DetailInfoRow
          icon={Users}
          title={eventAvailability(scheduleOccurrence).label ?? ""}
        />
      ) : null}
      {hasScheduleTimeline ? (
        <BookingWindowRow
          occurrence={bookingWindowOccurrence}
          upcoming={bookingWindowOccurrences}
          onOpenSchedule={() => setScheduleOpen(true)}
        />
      ) : null}

      <ContactDetailsRow
        details={eventContactDetails}
        subtitle="Reach the organiser and follow along"
        onOpen={() => setContactOpen(true)}
      />

      {(() => {
        const cards = [
          {
            label: "Why this event stands out",
            value: event.highlightStandsOut,
            icon: Crown,
          },
          {
            label: "What you'll experience",
            value: event.highlightExperience,
            icon: Sparkles,
          },
          {
            label: "Special attractions",
            value: event.highlightSpecialAttractions,
            icon: Star,
          },
          {
            label: "Perfect for",
            value: event.highlightPerfectFor,
            icon: Users,
          },
        ].filter((card) => card.value != null && card.value.trim().length > 0);

        if (cards.length === 0) return null;

        // A one-card carousel is all downside: the 88% basis leaves a dead
        // gutter where a second card would peek, and there is nothing to
        // swipe to. Render the single card full-bleed-free instead.
        if (cards.length === 1) {
          const [card] = cards;
          return (
            <HighlightCard
              label={card.label}
              value={card.value ?? ""}
              Icon={card.icon}
              onOpen={() =>
                setActiveHighlight({
                  label: card.label,
                  value: card.value ?? "",
                })
              }
            />
          );
        }

        // Card width follows this column, not the viewport: beside the media
        // (iPhone Duo open, ~350pt) a viewport `md:` basis made ~160pt cards
        // that broke every word onto its own line.
        return (
          <div className="@container">
            <Carousel
              opts={{ align: "start", dragFree: true }}
              className="-mx-3"
            >
              <CarouselContent className="-ml-2 px-3">
                {cards.map((card) => (
                  <CarouselItem
                    key={card.label}
                    className="basis-[88%] pl-2 @lg:basis-[68%] @xl:basis-[46%]"
                  >
                    <HighlightCard
                      label={card.label}
                      value={card.value ?? ""}
                      Icon={card.icon}
                      onOpen={() =>
                        setActiveHighlight({
                          label: card.label,
                          value: card.value ?? "",
                        })
                      }
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>
        );
      })()}

      <div className="space-y-3">
        <h3 className="text-xl font-bold text-text-primary dark:text-white">
          About the event
        </h3>
        <p className="line-clamp-4 text-sm leading-relaxed whitespace-pre-line text-text-secondary dark:text-white/65">
          {event.description}
        </p>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-sm font-semibold text-text-primary dark:text-white"
          onClick={() => setAboutOpen(true)}
        >
          Read more
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Optional extras (food, merch). Right after the pitch, before the
          small print: this is where somebody deciding to go is still reading,
          and seeing "breakfast burritos at the wall" is part of the pitch. */}
      <EventAddonsShowcase />

      {hasThingsOrAmenities ? (
        <div className="space-y-3">
          <h3 className="text-xl font-bold text-text-primary dark:text-white">
            Things to Know
          </h3>
          {previewItems.length > 0 ? (
            <div className="divide-y divide-border dark:divide-white/[0.08]">
              {previewItems.map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-3 py-3.5 text-sm text-text-secondary dark:text-white/75"
                >
                  <span className="h-4 w-0.5 shrink-0 rounded-full bg-brand/35 dark:bg-white/20" />
                  {item}
                </div>
              ))}
            </div>
          ) : null}
          {event.thingsToKnow.length > 4 || hasAmenities ? (
            <button
              type="button"
              className="inline-flex items-center gap-1 text-sm font-semibold text-text-primary dark:text-white"
              onClick={() => setThingsToKnowOpen(true)}
            >
              See all
              <ChevronRight className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      ) : null}

      {/* A pack only makes sense as a comparison with the price of booking once,
          so it belongs on the page where that price is being weighed — directly
          above the hiive that sells it. */}
      {/* Suppressed for a member whose pack already covers this event: the
          note at the top of the page has told them so, and a list of packs to
          buy underneath it reads as though the first one did not count. */}
      {packs.length > 0 && hiiveSlug && !viewerPackHolding ? (
        <PackWalletCards
          packs={packs}
          hiiveSlug={hiiveSlug}
          heading="Save with a pack"
          context={packStripContext ?? undefined}
        />
      ) : null}

      {hiive && (
        <HostedByCard
          hiive={{
            name: hiive.name,
            logoImage: hiive.logoImage,
            slug: hiiveSlug ?? "",
          }}
          eventCount={hiiveEventCount}
          memberCount={hiiveMemberCount}
        />
      )}

      {event.collaborators?.length ? (
        <CollaboratorsCard collaborators={event.collaborators} />
      ) : null}

      {event.termsAndConditions.length > 0 ||
      (event.faqs && event.faqs.length > 0) ? (
        <div className="space-y-3">
          <h3 className="text-xl font-bold text-text-primary dark:text-white">
            More
          </h3>
          <DetailInfoGroup>
            {event.termsAndConditions.length > 0 ? (
              <DetailInfoRow
                icon={FileText}
                size="sm"
                title="Terms and conditions"
                onClick={() => setTermsOpen(true)}
                className={DETAIL_INFO_GROUP_ROW}
              />
            ) : null}
            {event.faqs && event.faqs.length > 0 ? (
              <DetailInfoRow
                icon={HelpCircle}
                size="sm"
                title="Frequently asked questions"
                onClick={() => setFaqsOpen(true)}
                className={DETAIL_INFO_GROUP_ROW}
              />
            ) : null}
          </DetailInfoGroup>
        </div>
      ) : null}

      <Drawer open={locationOpen} onOpenChange={setLocationOpen}>
        <DrawerContent
          overlayClassName="z-[1190]"
          className="z-[1200] max-h-[85vh] border-border bg-surface text-text-primary shadow-xl dark:border-white/[0.14] dark:bg-[#0f1218] dark:text-white"
        >
          <DrawerHeader className="flex flex-row items-center justify-between border-b border-border px-4 py-3 text-left dark:border-white/[0.1]">
            <DrawerTitle className="text-xl leading-tight text-text-primary dark:text-white">
              Location
            </DrawerTitle>
          </DrawerHeader>
          <div className="space-y-4 overflow-y-auto p-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
            {/*
              No map for an event that withheld its pin. The coordinates are
              not merely hidden here — the server never sent them — so there is
              nothing to draw even if this branch were wrong.
            */}
            {event.hideExactLocation ||
            event.latitude == null ||
            event.longitude == null ? (
              <div className="rounded-2xl border border-dashed border-border px-4 py-6 text-center">
                <MapPinOff
                  className="mx-auto size-5 text-text-tertiary"
                  aria-hidden
                />
                <p className="mt-2 text-sm font-medium text-text-primary dark:text-white">
                  Exact location shared with attendees
                </p>
                <p className="mt-1 text-xs text-text-secondary dark:text-white/60">
                  The organisers send the address once you have a ticket.
                </p>
              </div>
            ) : (
              <VenueMap
                latitude={event.latitude}
                longitude={event.longitude}
                venueName={event.venueName}
                address={event.address}
              />
            )}
            <div className="space-y-1">
              <p className="text-base font-medium text-text-primary dark:text-white">
                {event.venueName}
              </p>
              <p className="text-sm text-text-secondary dark:text-white/70">
                {event.address}
              </p>
              {remoteDiscoveryNote ? (
                <p className="text-xs text-text-tertiary dark:text-white/50">
                  {remoteDiscoveryNote}
                </p>
              ) : null}
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      <Drawer open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DrawerContent
          overlayClassName="z-[1190]"
          className="z-[1200] mx-auto max-h-[86vh] max-w-2xl rounded-t-[30px] border-border bg-surface/96 text-text-primary shadow-xl backdrop-blur-2xl dark:border-white/[0.14] dark:bg-[#111111]/96 dark:text-white dark:shadow-2xl"
        >
          <DrawerHeader className="flex flex-row items-center justify-between px-6 pt-6 pb-2 text-left">
            <div>
              <DrawerTitle className="text-[1.35rem] leading-tight font-bold text-text-primary dark:text-white">
                Schedule and timeline
              </DrawerTitle>
              <DrawerDescription className="sr-only">
                Full event schedule, booking windows, and timeline entries.
              </DrawerDescription>
            </div>
          </DrawerHeader>
          <div className="overflow-y-auto px-6 pb-[calc(env(safe-area-inset-bottom)+1.5rem)]">
            {scheduleRange ? (
              <div className="mb-7 flex items-center gap-3 text-text-secondary dark:text-white/88">
                <Calendar className="h-5 w-5 shrink-0" />
                <span className="text-[1.05rem]">{scheduleRange}</span>
              </div>
            ) : null}

            <div className="space-y-5">
              {visibleScheduleOccurrences.map((occurrence) => {
                const anchor = getOccurrenceScheduleAnchor(occurrence);
                const expanded = expandedScheduleId === occurrence.id;
                const items = buildScheduleTimelineItems(occurrence);

                return (
                  <section key={occurrence.id} className="space-y-4">
                    <button
                      type="button"
                      className="flex w-full items-start justify-between gap-4 text-left"
                      onClick={() =>
                        setExpandedScheduleId((current) =>
                          current === occurrence.id ? null : occurrence.id,
                        )
                      }
                    >
                      <span className="min-w-0">
                        <span className="block text-[1.08rem] font-bold text-text-primary dark:text-white">
                          {SCHEDULE_SHORT_DATE_FORMATTER.format(anchor)} ·{" "}
                          {formatScheduleTime(occurrence.start)} –{" "}
                          {formatScheduleTime(occurrence.end)}
                        </span>
                        {/* The four timeline rows below make you subtract two
                            timestamps to learn this, and for an all-day event
                            it is the first thing worth knowing. */}
                        <span className="mt-0.5 block text-xs text-text-secondary dark:text-white/56">
                          Runs for{" "}
                          {formatDurationWords(
                            occurrence.end.getTime() -
                              occurrence.start.getTime(),
                          )}
                        </span>
                      </span>
                      {expanded ? (
                        <ChevronUp className="mt-1 h-5 w-5 shrink-0 text-text-secondary dark:text-white" />
                      ) : (
                        <ChevronDown className="mt-1 h-5 w-5 shrink-0 text-text-secondary dark:text-white" />
                      )}
                    </button>

                    {expanded ? <ScheduleTimeline items={items} /> : null}
                  </section>
                );
              })}
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      <Drawer open={aboutOpen} onOpenChange={setAboutOpen}>
        <DrawerContent
          overlayClassName="z-[1190]"
          className="z-[1200] max-h-[85vh] border-border bg-surface text-text-primary shadow-xl dark:border-white/[0.14] dark:bg-[#0f1218] dark:text-white"
        >
          <DrawerHeader className="flex flex-row items-center justify-between border-b border-border px-4 py-3 text-left dark:border-white/[0.1]">
            <DrawerTitle className="text-xl leading-tight text-text-primary dark:text-white">
              About this event
            </DrawerTitle>
          </DrawerHeader>
          <div className="overflow-y-auto p-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
            <p className="text-sm leading-relaxed whitespace-pre-wrap text-text-secondary dark:text-white/75">
              {event.description}
            </p>
          </div>
        </DrawerContent>
      </Drawer>

      <Drawer
        open={activeHighlight != null}
        onOpenChange={(open) => {
          if (!open) setActiveHighlight(null);
        }}
      >
        <DrawerContent
          overlayClassName="z-[1190]"
          className="z-[1200] max-h-[85vh] border-border bg-surface text-text-primary shadow-xl dark:border-white/[0.14] dark:bg-[#0f1218] dark:text-white"
        >
          <DrawerHeader className="flex flex-row items-center justify-between border-b border-border px-4 py-3 text-left dark:border-white/[0.1]">
            <DrawerTitle className="text-xl leading-tight text-text-primary dark:text-white">
              {activeHighlight?.label}
            </DrawerTitle>
          </DrawerHeader>
          <div className="overflow-y-auto p-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
            <p className="text-sm leading-relaxed whitespace-pre-wrap text-text-secondary dark:text-white/75">
              {activeHighlight?.value}
            </p>
          </div>
        </DrawerContent>
      </Drawer>

      <ContactDetailsDrawer
        open={contactOpen}
        onOpenChange={setContactOpen}
        details={eventContactDetails}
        subjectLabel="organiser"
      />

      <Drawer open={thingsToKnowOpen} onOpenChange={setThingsToKnowOpen}>
        <DrawerContent
          overlayClassName="z-[1190]"
          className="z-[1200] max-h-[85vh] border-border bg-surface text-text-primary shadow-xl dark:border-white/[0.14] dark:bg-[#0f1218] dark:text-white"
        >
          <DrawerHeader className="flex flex-row items-center justify-between border-b border-border px-4 py-3 text-left dark:border-white/[0.1]">
            <DrawerTitle className="text-xl leading-tight text-text-primary dark:text-white">
              Things to know
            </DrawerTitle>
          </DrawerHeader>
          <div className="overflow-y-auto p-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
            {hasThingsToKnow ? (
              <div>
                <p className="text-[12px] font-medium tracking-[0.16em] text-text-tertiary uppercase dark:text-white/55">
                  Event Info
                </p>
                <div className="mt-2 divide-y divide-border dark:divide-white/[0.08]">
                  {event.thingsToKnow.map((item) => (
                    <div
                      key={item}
                      className="flex items-center gap-3 py-3.5 text-sm text-text-secondary dark:text-white/75"
                    >
                      <span className="h-4 w-0.5 shrink-0 rounded-full bg-brand/35 dark:bg-white/20" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            {hasAmenities ? (
              <div className={hasThingsToKnow ? "mt-6" : ""}>
                <p className="text-[12px] font-medium tracking-[0.16em] text-text-tertiary uppercase dark:text-white/55">
                  Amenities
                </p>
                <div className="mt-2 divide-y divide-border dark:divide-white/[0.08]">
                  {event.amenities.map((item) => (
                    <div
                      key={item}
                      className="flex items-center gap-3 py-3.5 text-sm text-text-secondary dark:text-white/75"
                    >
                      <span className="h-4 w-0.5 shrink-0 rounded-full bg-brand/35 dark:bg-white/20" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </DrawerContent>
      </Drawer>

      <Drawer open={termsOpen} onOpenChange={setTermsOpen}>
        <DrawerContent
          overlayClassName="z-[1190]"
          className="z-[1200] max-h-[85vh] border-border bg-surface text-text-primary shadow-xl dark:border-white/[0.14] dark:bg-[#0f1218] dark:text-white"
        >
          <DrawerHeader className="flex flex-row items-center justify-between border-b border-border px-4 py-3 text-left dark:border-white/[0.1]">
            <DrawerTitle className="text-xl leading-tight text-text-primary dark:text-white">
              Terms and conditions
            </DrawerTitle>
          </DrawerHeader>
          <div className="overflow-y-auto p-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
            <div className="divide-y divide-border dark:divide-white/[0.08]">
              {event.termsAndConditions.map((item) => (
                <div
                  key={`${item.title}-${item.description ?? ""}`}
                  className="py-3.5"
                >
                  <p className="text-sm font-medium text-text-primary dark:text-white">
                    {item.title}
                  </p>
                  {item.description ? (
                    <p className="mt-1 text-sm text-text-tertiary dark:text-white/55">
                      {item.description}
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      <Drawer open={faqsOpen} onOpenChange={setFaqsOpen}>
        <DrawerContent
          overlayClassName="z-[1190]"
          className="z-[1200] max-h-[85vh] border-border bg-surface text-text-primary shadow-xl dark:border-white/[0.14] dark:bg-[#0f1218] dark:text-white"
        >
          <DrawerHeader className="flex flex-row items-center justify-between border-b border-border px-4 py-3 text-left dark:border-white/[0.1]">
            <DrawerTitle className="text-xl leading-tight text-text-primary dark:text-white">
              Frequently Asked Questions
            </DrawerTitle>
          </DrawerHeader>
          <div className="overflow-y-auto p-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
            <Accordion type="single" collapsible className="w-full">
              {event.faqs?.map((item, index) => (
                <AccordionItem
                  key={item.title}
                  value={`item-${index}`}
                  className="border-border dark:border-glass-border-08"
                >
                  <AccordionTrigger className="text-text-primary hover:no-underline dark:text-white">
                    {item.title}
                  </AccordionTrigger>
                  <AccordionContent className="leading-relaxed whitespace-pre-line text-text-secondary dark:text-white/60">
                    {item.description}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
